// /site/* API 签名 client（网页端专用）
// 签名规则（与 woofootball_api 后端 site 中间件一致，规则源 doc/login-validation.md 附录）：
//   - 参数：appkey / timestamp / nonce（登录后 + token_hash），无 version / platform
//   - 算法：HMAC-SHA256（与 sign.ts 1:1，密钥 = VITE_HMAC_SECRET_WEB）
//   - 登录后请求头：Authorization: Bearer <token>（token_hash 参与签名）

import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import { sign, generateNonce, loadSecret } from './sign'
import { serverTime } from './serverTime'
import { API_BASE_URL, SITE_APPKEY } from '@/config'
import { useAuthStore } from '@/stores/auth'
import { routerHolder } from '@/router/instance'

// 40122 签名超时自愈重试使用的配置标记类型
type RetryCfg = InternalAxiosRequestConfig & { _retriedSignTimeSkew?: boolean }

// 登录态失效（token 类子码）：40101 未登录 / 40102 过期 / 40103 验证失败 / 40104 未生效 /
// 40105 无效令牌 / 40106 缺信息 / 40107 令牌已失效或不存在（一端登出清除 token 后命中）
const SESSION_INVALID_MIN = 40101
const SESSION_INVALID_MAX = 40107

/** 是否命中 token 类会话失效子码（签名类 40120-40125 不算） */
function isSessionInvalidSubCode(subCode: unknown): boolean {
  return typeof subCode === 'number' && subCode >= SESSION_INVALID_MIN && subCode <= SESSION_INVALID_MAX
}

/** 从响应 body 提取会话失效信息（HTTP 401 或 body.code 401，均视为会话问题） */
function isSessionInvalidResponse(status: number | undefined, body: unknown): boolean {
  const b = (body ?? {}) as { code?: unknown; sub_code?: unknown }
  const code401 = status === 401 || b.code === 401
  return code401 && isSessionInvalidSubCode(b.sub_code)
}

/**
 * 滑动续期（后端 active_extend）：消费响应头里的轮换 token。
 * 轮换时后端下发 X-New-Token / X-Token-Expires-In / X-Token-Hash，这里更新本地
 * 登录态并持久化 —— 活跃用户无需重新登录即自动续期（7 天不活跃则由后端 401 判定过期）。
 * 防护：
 *  - 仅当本地仍持有本次请求所用的 token 才采纳（防登出/过期后被迟到响应"复活"会话）；
 *  - 长度 / JWT 形状护栏，防异常 header 撑爆本地存储。
 */
function adoptRotatedToken(response: {
  headers?: Record<string, unknown>
  config?: InternalAxiosRequestConfig & { _sentToken?: string }
}) {
  const headers = response?.headers ?? {}
  const newToken = headers['x-new-token']
  if (typeof newToken !== 'string' || !newToken) return
  if (newToken.length > 4096 || newToken.split('.').length !== 3) return
  try {
    const auth = useAuthStore()
    const sentToken = response?.config?._sentToken
    if (sentToken && auth.token !== sentToken) return
    const expiresIn = Number(headers['x-token-expires-in'] || 0)
    const tokenHash = typeof headers['x-token-hash'] === 'string' ? headers['x-token-hash'] : ''
    auth.applyRotatedToken({ token: newToken, tokenHash, expiresIn })
  } catch {
    // pinia 未注册 → 静默
  }
}

export const siteHttp: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
})

siteHttp.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const timestamp = String(serverTime.getTimestampSeconds())
  const nonce = generateNonce()

  const signParams: Record<string, unknown> = {
    appkey: SITE_APPKEY,
    timestamp,
    nonce
  }

  try {
    const auth = useAuthStore()
    if (auth.isLoggedIn && auth.token) {
      // 登录后：token_hash 参与签名 + Authorization Bearer
      signParams.token_hash = auth.tokenHash || ''
      config.headers.set('Authorization', `Bearer ${auth.token}`)
      // 记录本次请求所用的 token，续期采纳时校验（防登出/过期后被迟到响应复活会话）
      ;(config as InternalAxiosRequestConfig & { _sentToken?: string })._sentToken = auth.token
    }
  } catch {
    // pinia 未注册（SSR/极早期）→ 静默
  }

  const sig = await sign(signParams, loadSecret())

  config.headers.set('timestamp', timestamp)
  config.headers.set('nonce', nonce)
  config.headers.set('appkey', SITE_APPKEY)
  config.headers.set('sign', sig)

  return config
})

siteHttp.interceptors.response.use(
  (response) => {
    // 同步服务器时间（偏移校准，供签名 timestamp 使用）
    const dateHeader = response.headers['date']
    serverTime.syncFromResponse(typeof dateHeader === 'string' ? dateHeader : null)

    // 滑动续期：轮换 token 采纳（活跃用户自动续期）
    adoptRotatedToken(response)

    // 后端业务错误统一返回 HTTP 200 + body.code != 200（ValidateException/AuthException render）
    // axios 不会抛错，这里显式检查并转为 Promise.reject，业务层才能捕获到 msg。
    const body = response.data
    if (body && typeof body.code === 'number' && body.code !== 200) {
      // 防御：HTTP 200 但 body.code=401 + token 类子码 → 同样视为会话失效（统一清理 + 跳登录页）
      if (isSessionInvalidResponse(response.status, body)) {
        handleSessionExpired()
      }
      const err = new Error(typeof body.msg === 'string' && body.msg ? body.msg : '请求失败')
      ;(err as Error & { response?: unknown }).response = response
      return Promise.reject(err)
    }
    return response
  },
  (error) => {
    // 登录态失效处理（账号级同步核心）：任一端登出清除 token 后，
    // 本端请求登录态接口 → 中间件返回 HTTP 401 + sub_code（40101-40107 token 类）。
    // 前端识别后清除本地登录态 + 提示 + 跳登录页；签名类（40120-40125）不触发登出。
    const status = error?.response?.status
    const body = error?.response?.data

    // 40122 签名超时自愈：用响应 Date 头强制校准 offset 后换新 nonce 重发一次
    const isTimeSkew = status === 401 &&
      body && (body.sub_code === 40122 || body.code === 40122)
    if (isTimeSkew && !(error.config as RetryCfg)?._retriedSignTimeSkew) {
      const dateHeader = error.response.headers.date as string | undefined
      serverTime.forceSyncFromResponse(dateHeader)
      const retryCfg = {
        ...error.config,
        _retriedSignTimeSkew: true
      } as RetryCfg
      return siteHttp(retryCfg)
    }

    // 仅明确命中 token 类子码（40101-40107）才登出；裸 401 / 签名类（40120-40125）不误登出
    if (isSessionInvalidResponse(status, body)) {
      handleSessionExpired()
    }
    return Promise.reject(error)
  }
)

/**
 * 登录态失效统一处理：清本地 → 跳登录页（带 redirect 回当前页 + reason=expired 提示）
 * 并发多个 401 时只处理一次（clear 后 token 为空，后续 401 只清理不再跳转）。
 * 无论本地是否已判定为未登录，都先 clear()——本地 token 已过期但残留 localStorage 时，
 * 请求不会携带凭证、命中 40101，此时也需清掉残留，避免"半登出"脏状态。
 */
function handleSessionExpired() {
  const auth = useAuthStore()
  const hadToken = !!auth.token
  auth.clear()
  const router = routerHolder.instance
  if (!router) return
  const current = router.currentRoute.value
  if (current.name === 'login') return // 已在登录页，不重复跳转
  if (!hadToken) return // 本地本无凭证 → 仅清理残留，不跳转
  const redirect = current.fullPath
  const query: Record<string, string> = { reason: 'expired' }
  if (redirect && redirect !== '/') query.redirect = redirect
  void router.replace({ path: '/login', query })
}

export default siteHttp
